import { Component, Input, isDevMode, OnChanges, SimpleChanges } from '@angular/core';
import { Filter, Displayable } from '../../models/filter-models'; // Import Tier enum
import { DisplayType, getEndgameContentIcon } from "../../models/enums";
import { FormsModule } from '@angular/forms';
import { ItemDataService } from '../../services/item-data.service';
import { ItemData, TierList, TierData, TieredItem, TierItemTypeGroup, Tier, TierValueList } from '../../models/item-data-models'; // Import TierData type
import { ItemLabelComponent } from '../../common/item-label/item-label.component';
import { FilterService } from '../../services/filter.service';
import { TierStyleComponent } from '../../common/tier-style/tier-style.component';
import { currencyDisplayLabelMap } from './display-label-map';
import { CurrencyTierService } from '../../services/tiering/currency-tier.service';
import { CurrencyTieringMode, CurrencyTypeSelectionMode } from "../../models/enums";
import { PriceTypeIndicatorComponent } from '../../common/price-type-indicator/price-type-indicator.component';
import { TooltipWrapperComponent } from '../../common/tooltip-wrapper/tooltip-wrapper.component';
import { DivCardViewerComponent } from '../../common/divcard-viewer/divcard-viewer.component';
import { getTierColor } from '../../common/helper';
import { SelectComponent, SelectOptionComponent } from '../../common/select/select.component';
import { AutocompleteComponent, AutocompleteOption } from '../../common/autocomplete/autocomplete.component';
import { BaseComponent } from '../../common/base.component';
import { GlobalShortcutFocusDirective } from '../../common/global-shortcut-focus.directive';
import { EconomyService } from '../../services/economy.service';
import { Economy } from '../../models/economy-models';
import { CustomCurrencyTieringModeDialogComponent } from '../../custom-currency-tiering-mode-dialog/custom-currency-tiering-mode-dialog.component';
import { DynamicContentComponent } from '../../common/dynamic-content/dynamic-content.component';
import { hasMovedFromOriginalTier } from '../tier-helpers';
import { LOCAL_STORAGE_KEY_CURRENCY_TIER_LIST_CUSTOM_TYPES, LOCAL_STORAGE_KEY_CURRENCY_TIER_LIST_SELECTION_MODE } from '../../const';

interface TieredItemVm extends TieredItem {
  unique?: boolean;
  image?: string;
  value?: number;
  displayLabel?: string;
  hiddenByQuickFilter?: boolean;
  previousTierColor?: string;
  previousTierColorAdjusted?: string;
  movedUpArray?: number[];
  movedDownArray?: number[];
  movedUpAdjustedArray?: number[];
  movedDownAdjustedArray?: number[];
}

interface TierItemTypeGroupVm extends TierItemTypeGroup {
  baseTypes: TieredItemVm[];
  contentIcon?: string;
}

interface TierDataVm extends TierData {
  name: string;
  styleDialogTitle: string;
  priceLabel: string;
  priceBaseCurrency: string;
  priceBaseCurrencyImage: string;
  itemTypes: TierItemTypeGroupVm[];
  isDragOver?: boolean;
  dragCounter?: number;
  cosmetics: Displayable;
  exampleName: string;
  show: boolean;
  tierColor?: string;
}

@Component({
  selector: 'app-currency-tiers',
  templateUrl: './currency-tiers.component.html',
  styleUrls: ['./currency-tiers.component.scss', '../tier-tab-common.scss'],
  imports: [FormsModule, ItemLabelComponent, PriceTypeIndicatorComponent, TierStyleComponent, TooltipWrapperComponent,
    DivCardViewerComponent, SelectComponent, SelectOptionComponent, AutocompleteComponent, GlobalShortcutFocusDirective,
    CustomCurrencyTieringModeDialogComponent, DynamicContentComponent]
})
export class CurrencyTiersComponent extends BaseComponent implements OnChanges {
  @Input({ required: true }) filter!: Filter;
  @Input({ required: true }) divinationCardTab!: boolean;
  @Input() changedOnly = false;

  itemData!: ItemData[];
  tierListData: TierList;
  tierList: TierDataVm[] = [];
  draggedItem: TieredItemVm | null = null;
  tierColumns: TierDataVm[][] = [];
  customTierColumns: TierDataVm[] = [];
  selectedTierValueList: TierValueList = [];
  tierCutoffValues: { mode: CurrencyTieringMode, values: TierValueList }[] = [];
  inflation!: number;
  isDevMode = isDevMode();

  divinationCardFilter = '';
  pinnedDivinationCard = '';
  showCustomTieringDialog = false;

  currencyTypeSelection = 'Common';
  readonly currencyTypePresetModes: CurrencyTypeSelectionMode[] = ['All', 'Common', 'Droppable', 'Custom'];
  currencyTypeSelectOptions: { type: string, image?: string }[] = [];
  customSelectedCurrencyTypes: string[] = [];
  currencyTypeAutocompleteOptions: AutocompleteOption[] = [];
  hidableCurrencyTypes: string[];
  notDroppableCurrencyTypes: string[];
  ruthlessDisabledCurrencyTypes: string[];
  private customTypesLoadedFromStorage = false;

  DisplayType = DisplayType;
  Tier = Tier;

  constructor(private itemDataService: ItemDataService, private filterService: FilterService,
    private currencyTierService: CurrencyTierService, private economyService: EconomyService) {
    super();
    this.itemData = this.itemDataService.getItemData();
    this.hidableCurrencyTypes = this.itemDataService.getHidableCurrencyTypes();
    this.notDroppableCurrencyTypes = this.itemDataService.getNotDroppableCurrencyTypes();
    this.ruthlessDisabledCurrencyTypes = this.itemDataService.getRuthlessDisabledCurrencyTypes();
    this.tierListData = this.currencyTierService.getTierList();
    this.inflation = this.economyService.getInflation() ?? 0;
    this.tierCutoffValues = this.currencyTierService.getTierCutoffValues();
    this.loadCurrencyTypeSelectionFromStorage();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['filter'] || changes['changedOnly']) {
      this.buildTierList();
    }
  }

  buildTierList(): void {
    this.buildCurrencyTypeSelectOptions();
    this.buildCurrencyTypeAutocompleteOptions();
    this.ensureCustomTypesDefault();

    this.selectedTierValueList = this.currencyTierService.getSelectedTierValueList();

    const tierList = this.tierListData.map(tier => {
      const tierConfig = (this.divinationCardTab ? this.filter.divCardTierCosmetics : this.filter.tierCosmetics).find(c => c.tier === tier.tier)!;
      const tierName = tierConfig.displayOptions.hidden
        ? 'Hidden'
        : tierConfig.displayOptions.cosmeticOptions?.highlight
          ? tierConfig.displayOptions.cosmeticOptions?.highlightColor
          : 'Visible';

      const { priceLabel, priceBaseCurrency, priceBaseCurrencyImage } = this.currencyTierService.getDisplayValue(tier.tier);

      return {
        ...tier,
        name: (tier.tier + '-Tier - ') + tierName,
        styleDialogTitle: tier.tier + '-Tier',
        priceLabel,
        priceBaseCurrency,
        priceBaseCurrencyImage,
        cosmetics: tierConfig,
        exampleName: this.divinationCardTab ? tier.itemTypes.find(i => i.itemType === 'Divination Cards')?.baseTypes[0]?.name : tier.itemTypes[0]?.baseTypes[0]?.name,
        show: tierConfig.displayOptions.hidden,
        tierColor: getTierColor(this.filter, tier.tier, this.divinationCardTab),
        itemTypes: tier.itemTypes
          .filter(i => this.shouldShowCurrencyItemType(i.itemType))
          .map(itemType => ({
            ...itemType,
            contentIcon: this.getItemTypeContentIcon(itemType.itemType),
            baseTypes: itemType.baseTypes
              .map(baseType => {
                const image = this.itemDataService.getFlatItemDataByBaseTypeName(baseType.name)?.baseType.image;

                const vm = {
                  ...baseType,
                  image,
                  value: baseType.value,
                  displayLabel: currencyDisplayLabelMap.find(c => c.name === baseType.name)?.displayLabel
                } as TieredItemVm;

                if (baseType.originalTier || this.filter.economy === Economy.Fixed || baseType.hiddenByQuickFilter) return vm;

                if (!vm.previousTier) return vm;

                vm.previousTierColor = getTierColor(this.filter, vm.previousTier, this.divinationCardTab);
                vm.movedUpArray = Array.from({ length: vm.movedUp || 0 }, (_, i) => i);
                vm.movedDownArray = Array.from({ length: vm.movedDown || 0 }, (_, i) => i);
                return vm;
              })
              .filter(bt => !this.changedOnly || hasMovedFromOriginalTier(bt.originalTier, tier.tier))
              .sort((a, b) => a.value && b.value ? b.value - a.value : a.value ? -1 : b.value ? 1 : 0)
          }))
          .filter(itemTypeGroup => itemTypeGroup.baseTypes.length > 0)
          .sort((a, b) => a.itemType === 'Uniques' ? 2 : b.itemType === 'S-Tier Uniques' ? 1 : b.itemType === 'Uniques' ? -1 : 0)
      } as TierDataVm;
    }).filter(t => !!t);

    this.tierColumns = [
      [], // S
      [], // A
      [], // B
      [], // C
      [], // D, E and F
    ];

    for (let tier of tierList) {
      if (tier.tier === Tier.SPurple) {
        this.tierColumns[0].push(tier);
      } else if (tier.tier === Tier.ARed) {
        this.tierColumns[1].push(tier);
      } else if (tier.tier === Tier.BOrange) {
        this.tierColumns[2].push(tier);
      } else if (tier.tier === Tier.CYellow) {
        this.tierColumns[3].push(tier);
      } else {
        this.tierColumns[4].push(tier);
      }
    }
  }

  updateFilter() {
    this.filterService.updateFilter();
  }

  onDragStart(event: DragEvent, baseType: TieredItemVm) {
    this.draggedItem = baseType;
    event.dataTransfer?.setData('baseType', JSON.stringify(baseType));
  }

  onDragEnd() {
    this.draggedItem = null;
  }

  onDragEnter(event: DragEvent, tier: TierDataVm) {
    event.preventDefault();
    tier.dragCounter = (tier.dragCounter || 0) + 1;
    tier.isDragOver = true;
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
  }

  onDragLeave(event: DragEvent, tier: TierDataVm) {
    tier.dragCounter = (tier.dragCounter || 0) - 1;
    if (tier.dragCounter === 0) {
      tier.isDragOver = false;
    }
  }

  onDrop(event: DragEvent, targetTier: TierDataVm) {
    event.preventDefault();
    targetTier.dragCounter = 0;
    targetTier.isDragOver = false;
    const baseTypeJson = event.dataTransfer!.getData('baseType');
    if (!baseTypeJson) return;
    const baseType = JSON.parse(baseTypeJson) as TieredItemVm;
    this.moveItemToTier(baseType, targetTier.tier || targetTier.name);
    this.draggedItem = null;
  }

  showItem(baseType: TieredItemVm) {
    this.filterService.updateCurrencyItemTier(baseType.name, Tier.EVisible, { shownExplicitly: true });
    this.tierListData = this.currencyTierService.getTierList();
    this.buildTierList();
  }

  hideItem(baseType: TieredItemVm) {
    this.filterService.updateCurrencyItemTier(baseType.name, Tier.FHidden, { shownExplicitly: false });
    this.tierListData = this.currencyTierService.getTierList();
    this.buildTierList();
  }

  resetToOriginalTier(baseType: TieredItemVm) {
    this.filterService.resetCurrencyItemTier(baseType.name);
    this.tierListData = this.currencyTierService.getTierList();
    this.buildTierList();
  }

  moveItemToTier(baseType: TieredItemVm, targetTier: Tier) {
    if (baseType.tier === targetTier) return;
    const fromManualF = baseType.tier === Tier.FHidden && !baseType.hiddenByQuickFilter;
    let options: { shownExplicitly?: boolean } | undefined;
    if (targetTier === Tier.FHidden) {
      options = { shownExplicitly: false };
    } else if (fromManualF && targetTier === Tier.EVisible) {
      options = { shownExplicitly: true };
    } else if (fromManualF) {
      options = { shownExplicitly: false };
    }
    this.filterService.updateCurrencyItemTier(baseType.name, targetTier, options);
    this.tierListData = this.currencyTierService.getTierList();
    this.buildTierList();
  }

  selectTieringMode(mode: CurrencyTieringMode) {
    this.currencyTierService.setTieringMode(mode, this.filter);
    this.buildTierList();
    this.updateFilter();
  }

  openCustomTieringDialog() {
    this.showCustomTieringDialog = true;
  }

  closeCustomTieringDialog() {
    this.showCustomTieringDialog = false;
    this.buildTierList();
    this.updateFilter();
  }

  onCurrencyTypeSelectionChange(selection: string) {
    this.currencyTypeSelection = selection;
    if (selection === 'Custom') {
      this.ensureCustomTypesDefault();
    }
    this.saveCurrencyTypeSelectionToStorage();
    this.buildTierList();
  }

  onCustomTypesChange(types: string[]) {
    this.customSelectedCurrencyTypes = types;
    this.customTypesLoadedFromStorage = true;
    this.saveCurrencyTypeSelectionToStorage();
    this.buildTierList();
  }

  toggleShowDropInfo() {
    this.settings.toggleShowDropInfo();
  }

  filterDivinationCard(baseType: TieredItemVm): boolean {
    return this.divinationCardFilter === ''
      || baseType.name.toLowerCase().includes(this.divinationCardFilter.toLowerCase())
      || (baseType.divCard?.text?.toLowerCase()?.includes(this.divinationCardFilter.toLowerCase()) ?? false)
      || (baseType.divCard?.dropInfo?.areas?.some(area => area.toLowerCase().includes(this.divinationCardFilter.toLowerCase())) ?? false)
      || (baseType.divCard?.dropInfo?.monsters?.some(monster => monster.toLowerCase().includes(this.divinationCardFilter.toLowerCase())) ?? false);
  }

  onDivinationCardClick(baseType: TieredItemVm) {
    this.pinnedDivinationCard = this.pinnedDivinationCard === baseType.name ? '' : baseType.name;
  }

  hideExplainer() {
    this.settings.dismissTierListExplainer();
  }

  private getAvailableCurrencyTypes(): string[] {
    return this.itemDataService.getTieredCurrencyItemTypes()
      .filter(t => t !== 'Divination Cards')
      .filter(t => !this.filter?.ruthless || !this.ruthlessDisabledCurrencyTypes.includes(t));
  }

  private getCommonCurrencyTypes(): string[] {
    return this.getAvailableCurrencyTypes()
      .filter(t => !this.hidableCurrencyTypes.includes(t));
  }

  private buildCurrencyTypeSelectOptions(): void {
    this.currencyTypeSelectOptions = this.getAvailableCurrencyTypes()
      .map(type => ({
        type,
        image: this.itemData.find(data => data.tierListItemType === type || data.itemType === type)?.baseTypes[0].image
      }));
  }

  private buildCurrencyTypeAutocompleteOptions(): void {
    this.currencyTypeAutocompleteOptions = this.getAvailableCurrencyTypes()
      .map(type => ({
        name: type,
        image: this.itemData.find(data => data.tierListItemType === type || data.itemType === type)?.baseTypes[0].image
      }));
  }

  private shouldShowCurrencyItemType(itemType: string): boolean {
    if (this.divinationCardTab) return itemType === 'Divination Cards';
    if (itemType === 'Divination Cards') return false;
    if (this.filter.ruthless && this.ruthlessDisabledCurrencyTypes.includes(itemType)) return false;

    if (!this.isPresetSelectionMode(this.currencyTypeSelection)) {
      return itemType === this.currencyTypeSelection;
    }

    switch (this.currencyTypeSelection) {
      case 'All':
        return true;
      case 'Common':
        return !this.hidableCurrencyTypes.includes(itemType);
      case 'Droppable':
        return !this.notDroppableCurrencyTypes.includes(itemType);
      case 'Custom':
        return this.customSelectedCurrencyTypes.includes('All') || this.customSelectedCurrencyTypes.includes(itemType);
    }
  }

  private isPresetSelectionMode(value: string): value is CurrencyTypeSelectionMode {
    return this.currencyTypePresetModes.includes(value as CurrencyTypeSelectionMode);
  }

  private loadCurrencyTypeSelectionFromStorage(): void {
    const storedSelection = localStorage.getItem(LOCAL_STORAGE_KEY_CURRENCY_TIER_LIST_SELECTION_MODE);
    if (storedSelection) {
      if (this.isPresetSelectionMode(storedSelection)) {
        this.currencyTypeSelection = storedSelection;
      } else if (this.isStoredCurrencyType(storedSelection)) {
        this.currencyTypeSelection = storedSelection;
      }
    }

    const storedCustom = localStorage.getItem(LOCAL_STORAGE_KEY_CURRENCY_TIER_LIST_CUSTOM_TYPES);
    if (storedCustom) {
      try {
        const parsed = JSON.parse(storedCustom) as string[];
        if (Array.isArray(parsed)) {
          this.customSelectedCurrencyTypes = parsed;
          this.customTypesLoadedFromStorage = true;
        }
      } catch {
        // ignore invalid stored value
      }
    }
  }

  private ensureCustomTypesDefault(): void {
    if (this.currencyTypeSelection === 'Custom' && !this.customTypesLoadedFromStorage && this.customSelectedCurrencyTypes.length === 0) {
      this.customSelectedCurrencyTypes = this.getCommonCurrencyTypes();
      this.saveCurrencyTypeSelectionToStorage();
    }
  }

  private isStoredCurrencyType(value: string): boolean {
    return this.itemDataService.getTieredCurrencyItemTypes()
      .filter(t => t !== 'Divination Cards')
      .includes(value);
  }

  private saveCurrencyTypeSelectionToStorage(): void {
    localStorage.setItem(LOCAL_STORAGE_KEY_CURRENCY_TIER_LIST_SELECTION_MODE, this.currencyTypeSelection);
    localStorage.setItem(LOCAL_STORAGE_KEY_CURRENCY_TIER_LIST_CUSTOM_TYPES, JSON.stringify(this.customSelectedCurrencyTypes));
  }

  private getItemTypeContentIcon(itemType: string): string | undefined {
    const content = this.itemDataService.getTierListItemTypeContent(itemType);
    return content ? getEndgameContentIcon(content) : undefined;
  }

  unhideFromQuickFilter(baseType: TieredItemVm, event: MouseEvent) {
    event.stopPropagation();
    if (!this.currencyTierService.unhideFromQuickFilter(this.filter, baseType.name)) return;
    this.currencyTierService.combineCurrencyTierListData(this.filter);
    this.tierListData = this.currencyTierService.getTierList();
    this.buildTierList();
    this.updateFilter();
  }
}
